import { useFrame, useThree } from '@react-three/fiber'
import { RefObject, useEffect, useRef } from 'react'
import {
    BufferAttribute, BufferGeometry, Color,
    Group,
    NormalBlending, Points, PointsMaterial,
} from 'three'
import { FootstepTimelineRef, _playerWorldPos, jumpDustSettings } from './footstepConfig'
import { useFootstepEffectTextures } from './useFootstepEffectTextures'

type EffectJumpBurst = {
    age: number
    geometry: BufferGeometry
    material: PointsMaterial
    points: Points
    positions: Float32Array
    velocities: Float32Array
}

interface EffectJumpProps {
    anchorRef: RefObject<Group | null>
    color?: string
    timelineRef: FootstepTimelineRef
}

export function EffectJump({ anchorRef, color, timelineRef }: EffectJumpProps) {
    const { scene } = useThree()

    const activeJumpBurstsRef = useRef<EffectJumpBurst[]>([])

    const { dustTexture } = useFootstepEffectTextures()

    useEffect(() => {
        return () => {
            for (const b of activeJumpBurstsRef.current) {
                scene.remove(b.points); b.geometry.dispose(); b.material.dispose()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function spawnJumpBurst(burstColor: string) {
        if (!anchorRef.current) return
        anchorRef.current.getWorldPosition(_playerWorldPos)
        const burstPosition = _playerWorldPos.clone()
        burstPosition.y += jumpDustSettings.yOffset

        const count = Math.max(1, Math.floor(jumpDustSettings.count))
        const positions = new Float32Array(count * 3)
        const velocities = new Float32Array(count * 3)
        const geometry = new BufferGeometry()
        geometry.setAttribute('position', new BufferAttribute(positions, 3))

        for (let i = 0; i < count; i++) {
            const i3 = i * 3
            const angle = (i / count) * Math.PI * 2 + (Math.random() * 0.6 - 0.3)
            const radius = Math.max(0, jumpDustSettings.ringRadius + (Math.random() * 2 - 1) * jumpDustSettings.ringRadiusJitter * 2)
            positions[i3] = Math.cos(angle) * radius
            positions[i3 + 1] = 0
            positions[i3 + 2] = Math.sin(angle) * radius
            const ox = Math.cos(angle), oz = Math.sin(angle)
            const ov = jumpDustSettings.outwardVelocity * (0.65 + Math.random() * 0.5)
            const tv = (Math.random() * 2 - 1) * jumpDustSettings.tangentialScatter * 2
            const uv = jumpDustSettings.upwardVelocity * (0.45 + Math.random() * 0.55) + (Math.random() * 2 - 1) * jumpDustSettings.verticalScatter * 2
            velocities[i3] = ox * ov + -oz * tv
            velocities[i3 + 1] = uv
            velocities[i3 + 2] = oz * ov + ox * tv
        }

        const material = new PointsMaterial({
            color: new Color(burstColor),
            size: jumpDustSettings.size,
            alphaMap: dustTexture,
            transparent: true,
            opacity: jumpDustSettings.opacity,
            depthWrite: false,
            blending: NormalBlending,
            sizeAttenuation: true,
            toneMapped: false,
        })
        const points = new Points(geometry, material)
        points.position.copy(burstPosition)
        scene.add(points)
        activeJumpBurstsRef.current.push({ age: 0, geometry, material, points, positions, velocities })
    }

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 60)
        const timeline = timelineRef.current

        for (const _jump of timeline.jumps) {
            spawnJumpBurst(color ?? jumpDustSettings.color)
        }

        for (let i = activeJumpBurstsRef.current.length - 1; i >= 0; i--) {
            const b = activeJumpBurstsRef.current[i]
            b.age += safeDelta
            const t = b.age / jumpDustSettings.lifetime
            if (t >= 1) {
                scene.remove(b.points); b.geometry.dispose(); b.material.dispose()
                activeJumpBurstsRef.current.splice(i, 1)
                continue
            }
            const drag = Math.exp(-jumpDustSettings.drag * safeDelta)
            for (let p = 0; p < b.positions.length; p += 3) {
                b.positions[p] += b.velocities[p] * jumpDustSettings.speed * safeDelta
                b.positions[p + 1] += b.velocities[p + 1] * jumpDustSettings.speed * safeDelta
                b.positions[p + 2] += b.velocities[p + 2] * jumpDustSettings.speed * safeDelta
                b.velocities[p] *= drag; b.velocities[p + 1] *= drag; b.velocities[p + 2] *= drag
            }
            b.geometry.attributes.position.needsUpdate = true
            b.material.opacity = jumpDustSettings.opacity * (1 - t)
            b.material.size = jumpDustSettings.size + (jumpDustSettings.endSize - jumpDustSettings.size) * t
        }
    })

    return null
}


